import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BookingEditorService } from '../../../../services/booking-editor/booking-editor.service';
import { UtilService } from '../../../../services/util.service';
import { CommercialDTO } from '../../../../services/api/api';
import { SbLocalDateTime } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { ColorPickerService } from '../../../../services/color-picker.service';
import { PermissionService } from '../../../../services/permission.service';

@Component({
  selector: 'sb-commercial-editor-forms',
  templateUrl: './sb-commercial-editor-forms.component.html',
  styleUrls: ['./sb-commercial-editor-forms.component.scss'],
  standalone: false,
})
export class SbCommercialEditorFormsComponent implements OnInit {
  @Output() formIsValid: EventEmitter<boolean> = new EventEmitter(false);
  now: SbLocalDateTime;
  orderingAvailable: boolean;
  commercialData: CommercialDTO;
  today = new SbLocalDateTime(DateTime.local());
  commercialDataFrom = new SbLocalDateTime(DateTime.local());
  commercialDataTill = new SbLocalDateTime(DateTime.local());
  showColorPicker: boolean;
  color: string;
  form = new UntypedFormGroup({
    Title: new UntypedFormControl({ value: '', disabled: this.verifyEditingStatus('Title') }, [Validators.required]),
    Duration: new UntypedFormControl({ value: '', disabled: this.verifyEditingStatus('Duration') }, [Validators.required]),
    From: new UntypedFormControl({ value: '', disabled: this.verifyEditingStatus('From') }, [Validators.required]),
    Till: new UntypedFormControl({ value: '', disabled: this.verifyEditingStatus() }, [Validators.required]),
  });
  private isREWEClient: boolean;

  constructor(
    public readonly bookingEditor: BookingEditorService,
    private readonly util: UtilService,
    public readonly translate: TranslateService,
    public readonly colorService: ColorPickerService,
    public readonly permissionService: PermissionService
  ) {
    this.isREWEClient = false;
  }

  get Title() {
    return this.form.get('Title');
  }

  get Duration() {
    return this.form.get('Duration');
  }

  get From() {
    return this.form.get('From');
  }

  get Till() {
    return this.form.get('Till');
  }

  ngOnInit(): void {
    this.isREWEClient = this.util.getSeatbackCMSClient() === 'rewe';
    this.now = new SbLocalDateTime();
    this.now.value = DateTime.local().plus({ day: 1 });
    this.commercialData = this.bookingEditor.editingRecord.get('commercial');
    if (!this.commercialData) {
      this.commercialData = new CommercialDTO();
      this.commercialData.from = new SbLocalDateTime(DateTime.fromJSDate(this.bookingEditor.editingRecord.get('startDate')));
      this.commercialData.till = new SbLocalDateTime(DateTime.fromJSDate(this.bookingEditor.editingRecord.get('endDate')));
      this.commercialData.active = true;
      this.commercialData.name = '';
      this.commercialData.duration = this.isREWEClient ? 6 : 10;
      this.bookingEditor.editingRecord.setAsync('commercial', this.commercialData);
    }
    this.registerFormChangesListener();
    this.updateForm();
  }

  setColor($event) {
    if ($event) {
      const temp = $event.split('#');
      this.commercialData.color = temp[temp.length - 1];
    }
  }

  cancel() {
    this.showColorPicker = false;
  }

  showColorPickerFn() {
    this.colorService.getColorSet();
    this.showColorPicker = true;
  }

  checkColorSavePermission(): boolean {
    return this.permissionService.hasPermission(this.permissionService.Permission.color_definition_create);
  }

  updateForm() {
    this.Title.setValue(this.commercialData.name);
    this.Duration.setValue(this.commercialData.duration);
    this.From.setValue(this.commercialData.from);
    this.Till.setValue(this.commercialData.till);
    this.color = '#' + this.commercialData.color;
  }

  private verifyEditingStatus(formType?: string): boolean {
    if (this.bookingEditor.readonly || this.bookingEditor.editingRecord.get('readonly')) {
      return true;
    }
    const now = new Date();
    switch (formType) {
      case 'Title': {
        return this.bookingEditor.editingRecord.get('startDate') < now;
      }
      case 'From': {
        return this.bookingEditor.editingRecord.get('startDate') < now;
      }
      case 'Duration': {
        return this.bookingEditor.editingRecord.get('startDate') < now;
      }
      default: {
        return this.bookingEditor.readonly || this.bookingEditor.editingRecord.get('readonly');
      }
    }
  }

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      this.commercialData.name = value;
    });
    this.Duration.valueChanges.subscribe(value => {
      this.commercialData.duration = value;
    });

    this.From.valueChanges.subscribe(value => {
      this.commercialData.from = value;
      if (
        this.commercialData?.from?.value &&
        this.commercialData?.till?.value &&
        this.commercialData.from.value > this.commercialData.till.value
      ) {
        this.commercialData.till = this.commercialData.from;
        this.commercialDataFrom = this.commercialData.from;
        this.From.setValue(this.commercialData.from);
        this.commercialDataTill = this.commercialData.till;
        this.Till.setValue(this.commercialData.till);
      }
    });

    this.Till.valueChanges.subscribe(value => {
      this.commercialData.till = value;
    });
    this.form.valueChanges.subscribe(() => {
      this.formIsValid.emit(this.form.valid);
    });
  }
}
